import React, { useState } from 'react';
import { X, Users, Plus } from 'lucide-react';
import { UserDto, MatchIdentifier, CreateUserMatchIdentifierRequest } from '../../types';
import { useAvailableMatchIdentifiers } from '../../hooks/use-user-match-identifiers';
import { DivisionUsersService } from '../../services/division-users-service';
import httpClient from '../../services/http-client';

interface BatchAssignIdentifiersModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  selectedUsers: UserDto[];
  onSuccess?: () => void;
}

export const BatchAssignIdentifiersModal: React.FC<BatchAssignIdentifiersModalProps> = ({
  isOpen,
  onClose,
  divisionId,
  selectedUsers,
  onSuccess
}) => {
  const [selectedIdentifierId, setSelectedIdentifierId] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [autoMatch, setAutoMatch] = useState<boolean>(false);
  const [isAssigning, setIsAssigning] = useState<boolean>(false);

  const { data: availableIdentifiers, isLoading } = useAvailableMatchIdentifiers(divisionId);
  const divisionUsersService = new DivisionUsersService(httpClient);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedIdentifierId || selectedUsers.length === 0) {
      alert('Please select a match identifier and ensure users are selected');
      return;
    }

    setIsAssigning(true);

    try {
      const assignmentData: CreateUserMatchIdentifierRequest = {
        matchIdentifierId: selectedIdentifierId,
        value: value || undefined,
        autoMatch
      };

      // Assign the identifier to each selected user individually
      const promises = selectedUsers.map(user => 
        divisionUsersService.assignMatchIdentifier(divisionId, user.id, assignmentData)
      );

      await Promise.all(promises);

      console.log(`Successfully assigned match identifier to ${selectedUsers.length} users`);
      onSuccess?.();
      onClose();
      setSelectedIdentifierId('');
      setValue('');
      setAutoMatch(false);
    } catch (error) {
      console.error('Failed to batch assign match identifiers:', error);
      alert('Failed to assign match identifiers. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedIdentifierId('');
    setValue('');
    setAutoMatch(false);
  };

  if (!isOpen) return null;

  const selectedIdentifier = availableIdentifiers?.matchIdentifiers?.find(
    id => id.id === selectedIdentifierId
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Batch Assign Match Identifier
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Selected Users Summary */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="font-medium text-blue-800">
              Selected Users ({selectedUsers.length})
            </span>
          </div>
          <div className="text-sm text-blue-700">
            {selectedUsers.map(user => `${user.firstName} ${user.lastName}`).join(', ')}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Available Match Identifiers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Select Match Identifier
            </label>
            
            {isLoading ? (
              <div className="text-center py-4 text-gray-500 dark:text-secondary-400">Loading...</div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-secondary-700 rounded-md p-2">
                {availableIdentifiers?.matchIdentifiers?.map((identifier: MatchIdentifier) => (
                  <div
                    key={identifier.id}
                    className={`p-3 rounded-md border cursor-pointer transition-colors ${
                      selectedIdentifierId === identifier.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                        : 'border-gray-200 dark:border-secondary-700 hover:border-gray-300 dark:hover:border-secondary-500 hover:bg-gray-50 dark:hover:bg-secondary-700'
                    }`}
                    onClick={() => setSelectedIdentifierId(identifier.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{identifier.name}</h4>
                        {identifier.description && (
                          <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">{identifier.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-100">
                            {identifier.type}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            identifier.active 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800' 
                              : 'bg-red-100 dark:bg-red-900 text-red-800'
                          }`}>
                            {identifier.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      {selectedIdentifierId === identifier.id && (
                        <div className="text-blue-500">
                          <Plus className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                  </div>
                )) || []}
              </div>
            )}
          </div>

          {/* Value Input */}
          <div>
            <label htmlFor="batchValue" className="block text-sm font-medium text-gray-700 dark:text-secondary-200">
              Value (Optional)
            </label>
            <input
              type="text"
              id="batchValue"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter value for this identifier (applied to all users)"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-secondary-400">
              This value will be assigned to all selected users. Leave empty for auto-matching.
            </p>
          </div>

          {/* Auto Match Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="batchAutoMatch"
              checked={autoMatch}
              onChange={(e) => setAutoMatch(e.target.checked)}
              className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            />
            <label htmlFor="batchAutoMatch" className="ml-2 block text-sm text-gray-700 dark:text-secondary-200">
              Enable auto-matching for all users
            </label>
          </div>

          {/* Preview */}
          {selectedIdentifier && (
            <div className="bg-gray-50 dark:bg-secondary-900 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Assignment Preview</h4>
              <div className="text-sm text-gray-600 dark:text-secondary-400 space-y-1">
                <div><span className="font-medium">Identifier:</span> {selectedIdentifier.name}</div>
                <div><span className="font-medium">Value:</span> {value || 'Auto-detect'}</div>
                <div><span className="font-medium">Auto-match:</span> {autoMatch ? 'Enabled' : 'Disabled'}</div>
                <div><span className="font-medium">Users:</span> {selectedUsers.length} selected</div>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedIdentifierId || selectedUsers.length === 0 || isAssigning}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAssigning ? 'Assigning...' : `Assign to ${selectedUsers.length} Users`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
